import React, { useCallback, useEffect, useState } from 'react';
import { AdvertisementAPI } from '../api/AdvertisementAPI';
import ConfirmDeleteModal from './dashboard/components/ConfirmDeleteModal';
import { endOfLocalDay, startOfLocalDay, toLocalDateInput } from '../utils/localDate';
import './dashboard/components/Marker-UI.css';
import '../components/advertisements/Advertisement-UI.css';
import PageTitle from '../components/PageTitle';

const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8083';

function resolveAssetUrl(path) {
  if (!path) return null;
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  return `${BASE_URL}${path.startsWith('/') ? '' : '/'}${path}`;
}

const AD_TYPE = 'Full Screen Pop Up';

// ── Status roadmap ────────────────────────────────────────────────────────────
const AD_STEPS = [
  { key: 'DRAFT',        label: 'Prüfung' },
  { key: 'INVOICE_SENT', label: 'Rechnung' },
  { key: 'PAID',         label: 'Bezahlen' },
  { key: 'LIVE',         label: 'Live'     },
  { key: 'ENDED',        label: 'Beendet'  },
];

const STEP_INDEX = Object.fromEntries(AD_STEPS.map((s, i) => [s.key, i]));

function AdRoadmap({ status }) {
  const isRejected = status === 'REJECTED';
  const isPaused   = status === 'PAUSED';
  const isScheduled = status === 'SCHEDULED';

  const effectiveStatus = isPaused ? 'LIVE' : isScheduled ? 'PAID' : status;
  const currentIdx = STEP_INDEX[effectiveStatus] ?? 0;

  return (
    <div className="adm-roadmap">
      {isRejected ? (
        <div className="adm-roadmap-rejected">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="11" height="11"><circle cx="12" cy="12" r="10"/><path d="M18 6L6 18M6 6l12 12"/></svg>
          Abgelehnt
        </div>
      ) : (
        AD_STEPS.map((step, i) => {
          const done    = i < currentIdx;
          const active  = i === currentIdx;
          const future  = i > currentIdx;
          const isLiveStep = step.key === 'LIVE' && isPaused;

          return (
            <React.Fragment key={step.key}>
              <div className={`adm-roadmap-step${done ? ' done' : active ? ' active' : ' future'}${isLiveStep ? ' paused' : ''}`}>
                <div className="adm-roadmap-dot">
                  {done ? (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" width="9" height="9"><path d="M20 6L9 17l-5-5"/></svg>
                  ) : isLiveStep ? <svg viewBox="0 0 24 24" fill="currentColor" width="9" height="9"><rect x="5" y="4" width="4" height="16" rx="1"/><rect x="15" y="4" width="4" height="16" rx="1"/></svg> : i + 1}
                </div>
                <span className="adm-roadmap-label">
                  {isLiveStep ? 'Pausiert' : step.label}
                  {step.key === 'PAID' && isScheduled ? ' · Geplant' : ''}
                </span>
              </div>
              {i < AD_STEPS.length - 1 && (
                <div className={`adm-roadmap-line${done ? ' done' : ''}`} />
              )}
            </React.Fragment>
          );
        })
      )}
    </div>
  );
}

// ── Ad Preview Modal ──────────────────────────────────────────────────────────
function AdPreviewModal({ sub, onClose }) {
  const [lang, setLang] = React.useState('DE');

  const image = resolveAssetUrl(
    lang === 'EN' && sub.advertisementImageENUrl
      ? sub.advertisementImageENUrl
      : sub.advertisementImageDEUrl
  );
  const text = lang === 'EN' ? sub.descriptionEN : sub.descriptionDE;
  const ctaLabel = lang === 'EN' ? 'Learn more' : 'Mehr erfahren';
  const adLabel = lang === 'EN' ? 'Ad' : 'Anzeige';

  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="adm-preview-overlay" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="adm-preview-modal">
        <div className="adm-preview-header">
          <div>
            <div className="adm-preview-title">{sub.displayName || sub.clientName}</div>
            <div className="adm-preview-sub">Vorschau · {sub.impressions?.toLocaleString('de-AT')} Impressionen</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div className="adm-lang-toggle">
              <button type="button" className={`adm-lang-btn${lang === 'DE' ? ' adm-lang-btn--active' : ''}`} onClick={() => setLang('DE')}>DE</button>
              <button type="button" className={`adm-lang-btn${lang === 'EN' ? ' adm-lang-btn--active' : ''}`} onClick={() => setLang('EN')}>EN</button>
            </div>
            <button className="cdm-modal-close" onClick={onClose} style={{ appearance: 'none', background: 'none', border: 'none', color: 'rgba(180,200,190,0.4)', cursor: 'pointer', fontSize: 20, padding: '0 4px' }}>×</button>
          </div>
        </div>

        <div className="adm-preview-body">
          <div className="adm-phone-frame">
            <div className="adm-phone-frame::before" />
            <div className="adm-phone-statusbar">
              <span>9:41</span>
              <span style={{ display: 'flex', gap: 4, alignItems: 'center', color: 'rgba(255,255,255,0.8)' }}>
                <svg viewBox="0 0 24 24" fill="currentColor" width="12" height="12"><path d="M1.5 8.5a13 13 0 0 1 21 0M5 12a10 10 0 0 1 14 0M8.5 15.5a6 6 0 0 1 7 0M12 19h.01"/></svg>
                <svg viewBox="0 0 24 24" fill="currentColor" width="12" height="12"><rect x="2" y="7" width="16" height="11" rx="2"/><path d="M22 11v3"/></svg>
              </span>
            </div>
            <div className="adm-phone-ad">
              {image ? (
                <img src={image} alt="Ad" className="adm-phone-img" />
              ) : (
                <div className="adm-phone-placeholder">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="40" height="40">
                    <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>
                  </svg>
                  <span>Kein Bild</span>
                </div>
              )}
              <div className="adm-phone-overlay" />
              <div className="adm-phone-ad-label">{adLabel}</div>
              <button className="adm-phone-close" type="button">✕</button>
              <div className="adm-phone-content">
                {sub.displayName && <div className="adm-phone-advertiser">{sub.displayName}</div>}
                {text && <p className="adm-phone-desc">{text}</p>}
                <div className="adm-phone-cta">
                  {ctaLabel}
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="11" height="11"><path d="M9 18l6-6-6-6"/></svg>
                </div>
              </div>
              <div className="adm-phone-home" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Review Modal ─────────────────────────────────────────────────────────────
function ReviewModal({ ad, sub, sess, onClose, onStatusAction, onInvoice }) {
  const [lang, setLang] = React.useState('DE');

  const image    = resolveAssetUrl(lang === 'EN' && ad.advertisementImageENUrl ? ad.advertisementImageENUrl : ad.advertisementImageDEUrl);
  const text     = lang === 'EN' ? ad.descriptionEN : ad.descriptionDE;
  const ctaLabel = lang === 'EN' ? 'Learn more' : 'Mehr erfahren';
  const adLabel  = lang === 'EN' ? 'Ad' : 'Anzeige';
  const statusCfg = AD_STATUS_CONFIG[ad.status] || AD_STATUS_CONFIG.DRAFT;

  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const Field = ({ label, value, full }) => value ? (
    <div className={`adm-rv-field${full ? ' adm-rv-field--full' : ''}`}>
      <span className="adm-rv-label">{label}</span>
      <span className="adm-rv-value">{value}</span>
    </div>
  ) : null;

  // Determine the primary CTA for the footer
  const primaryAction =
    ad.status === 'DRAFT'        ? { label: 'Akzeptieren',           cls: 'green', status: 'INVOICE_SENT', desc: 'Die Kampagne wird akzeptiert und eine Rechnung wird vorbereitet.' }
  : ad.status === 'INVOICE_SENT' ? { label: 'Als bezahlt markieren', cls: 'green', status: 'PAID',         desc: 'Damit bestätigst du, dass die Zahlung eingegangen ist.' }
  : (ad.status === 'PAID' || ad.status === 'SCHEDULED') ? { label: 'Live schalten', cls: 'green', status: 'LIVE', desc: 'Die Anzeige wird sofort in der App für Nutzer sichtbar.' }
  : ad.status === 'PAUSED'       ? { label: 'Fortsetzen',            cls: 'green', status: 'LIVE',         desc: 'Die Anzeige wird wieder in der App angezeigt.' }
  : null;

  return (
    <div className="adm-rv-overlay" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="adm-rv-modal">

        {/* ── Header ── */}
        <div className="adm-rv-header">
          <div className="adm-rv-header-left">
            <div className="adm-rv-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
            </div>
            <div>
              <div className="adm-rv-eyebrow">Kampagne prüfen</div>
              <div className="adm-rv-title">{ad.displayName || `#${ad.id}`}</div>
            </div>
          </div>
          <div className="adm-rv-header-right">
            <span className="adm-rv-status" style={{ background: statusCfg.bg, border: `1px solid ${statusCfg.border}`, color: statusCfg.color }}>
              {ad.status === 'LIVE' && <span className="adm-rv-status-dot" />}
              {statusCfg.label}
            </span>
            <div className="adm-lang-toggle">
              <button type="button" className={`adm-lang-btn${lang === 'DE' ? ' adm-lang-btn--active' : ''}`} onClick={() => setLang('DE')}>DE</button>
              <button type="button" className={`adm-lang-btn${lang === 'EN' ? ' adm-lang-btn--active' : ''}`} onClick={() => setLang('EN')}>EN</button>
            </div>
            <button className="adm-rv-close" onClick={onClose}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15"><path d="M18 6L6 18M6 6l12 12"/></svg>
            </button>
          </div>
        </div>

        {/* ── Body ── */}
        <div className="adm-rv-body">

          {/* Left: phone */}
          <div className="adm-rv-preview">
            <div className="adm-phone-frame">
              <div className="adm-phone-statusbar">
                <span>9:41</span>
                <span style={{ display:'flex', gap:4, alignItems:'center' }}>
                  <svg viewBox="0 0 24 24" fill="currentColor" width="11" height="11"><path d="M1.5 8.5a13 13 0 0 1 21 0M5 12a10 10 0 0 1 14 0M8.5 15.5a6 6 0 0 1 7 0M12 19h.01"/></svg>
                  <svg viewBox="0 0 24 24" fill="currentColor" width="11" height="11"><rect x="2" y="7" width="16" height="11" rx="2"/><path d="M22 11v3"/></svg>
                </span>
              </div>
              <div className="adm-phone-ad">
                {image
                  ? <img src={image} alt="" className="adm-phone-img" />
                  : <div className="adm-phone-placeholder">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="32" height="32"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                      <span>Kein Bild</span>
                    </div>
                }
                <div className="adm-phone-overlay" />
                <div className="adm-phone-ad-label">{adLabel}</div>
                <button className="adm-phone-close" type="button">✕</button>
                <div className="adm-phone-content">
                  {ad.displayName && <div className="adm-phone-advertiser">{ad.displayName}</div>}
                  {text && <p className="adm-phone-desc">{text}</p>}
                  <div className="adm-phone-cta">
                    {ctaLabel}
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="10" height="10"><path d="M9 18l6-6-6-6"/></svg>
                  </div>
                </div>
                <div className="adm-phone-home" />
              </div>
            </div>
          </div>

          {/* Right: info */}
          <div className="adm-rv-info">

            {/* Summary strip */}
            <div className="adm-rv-summary">
              <div className="adm-rv-summary-name">{ad.clientName || ad.displayName}</div>
              <div className="adm-rv-meta">
                {ad.companyName && ad.companyName !== ad.clientName && (
                  <span className="adm-rv-meta-chip">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="11" height="11"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>
                    {ad.companyName}
                  </span>
                )}
                {ad.impressionLimit > 0 && (
                  <span className="adm-rv-meta-chip adm-rv-meta-chip--green">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="11" height="11"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>
                    {ad.impressionLimit.toLocaleString('de-AT')} Impressionen
                  </span>
                )}
                {ad.startsAt && (
                  <span className="adm-rv-meta-chip">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="11" height="11"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                    {new Date(ad.startsAt).toLocaleDateString('de-AT')} – {ad.endsAt ? new Date(ad.endsAt).toLocaleDateString('de-AT') : '∞'}
                  </span>
                )}
              </div>
            </div>

            {/* Scrollable sections */}
            <div className="adm-rv-sections">

              {(ad.displayName || ad.clientName || ad.actionUrl) && (
                <div className="adm-rv-section">
                  <div className="adm-rv-section-title">Kampagne</div>
                  <div className="adm-rv-fields">
                    <Field label="Name"  value={ad.displayName} />
                    <Field label="Kunde" value={ad.clientName} />
                    {ad.actionUrl && (
                      <div className="adm-rv-field adm-rv-field--full">
                        <span className="adm-rv-label">CTA-Link</span>
                        <a href={ad.actionUrl} target="_blank" rel="noreferrer" className="adm-rv-link">
                          {ad.actionUrl}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {(ad.descriptionDE || ad.descriptionEN) && (
                <div className="adm-rv-section">
                  <div className="adm-rv-section-title">Anzeigentext</div>
                  <div className="adm-rv-fields">
                    <Field full label="Deutsch"  value={ad.descriptionDE} />
                    <Field full label="Englisch" value={ad.descriptionEN} />
                  </div>
                </div>
              )}

              {sub && (
                <div className="adm-rv-section">
                  <div className="adm-rv-section-title">Rechnungsdaten</div>
                  <div className="adm-rv-fields">
                    <Field full label="Firma"    value={`${sub.companyName || ''}${sub.legalForm ? ' ' + sub.legalForm : ''}`} />
                    <Field full label="Adresse"  value={[sub.street, `${sub.zip} ${sub.city}`, sub.country].filter(Boolean).join(', ')} />
                    <Field      label="E-Mail"   value={sub.invoiceEmail} />
                    <Field      label="Kontakt"  value={`${sub.contactPerson || ''}${sub.phone ? ' · ' + sub.phone : ''}`} />
                    {sub.vatId && <Field label="UID" value={sub.vatId} />}
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="adm-rv-footer">
          <div className="adm-rv-footer-left">
            {sub && ad.status !== 'DRAFT' && (
              <button className="adm-rv-btn adm-rv-btn--ghost" onClick={() => onInvoice(sub, ad.id)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="13" height="13"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                Rechnung erstellen
              </button>
            )}
            {ad.status === 'LIVE' && (
              <button className="adm-rv-btn adm-rv-btn--ghost" onClick={() => onStatusAction(ad.id, 'PAUSED', 'Anzeige pausieren', 'Die Anzeige wird vorübergehend ausgeblendet.', '#6b7280')}>
                ⏸ Pausieren
              </button>
            )}
            {['LIVE','PAUSED','SCHEDULED'].includes(ad.status) && (
              <button className="adm-rv-btn adm-rv-btn--ghost" onClick={() => onStatusAction(ad.id, 'ENDED', 'Kampagne beenden', 'Die Anzeige wird dauerhaft deaktiviert.', '#6b7280')}>
                ⏹ Beenden
              </button>
            )}
          </div>
          <div className="adm-rv-footer-right">
            {ad.status === 'DRAFT' && (
              <button className="adm-rv-btn adm-rv-btn--reject" onClick={() => onStatusAction(ad.id, 'REJECTED', 'Kampagne ablehnen', 'Die Kampagne wird abgelehnt und der Kunde wird informiert.', '#ef4444')}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="13" height="13"><path d="M18 6L6 18M6 6l12 12"/></svg>
                Ablehnen
              </button>
            )}
            {primaryAction && (
              <button
                className={`adm-rv-btn adm-rv-btn--primary adm-rv-btn--${primaryAction.cls}`}
                onClick={() => onStatusAction(ad.id, primaryAction.status, primaryAction.label, primaryAction.desc, '#3d8b5e')}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="13" height="13"><path d="M20 6L9 17l-5-5"/></svg>
                {primaryAction.label}
              </button>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}

// ── Status config ────────────────────────────────────────────────────────────
const AD_STATUS_CONFIG = {
  DRAFT:        { label: 'Prüfung',        color: 'rgba(180,200,190,0.4)',  bg: 'rgba(180,200,190,0.07)',  border: 'rgba(180,200,190,0.15)' },
  INVOICE_SENT: { label: 'Rechnung',       color: '#6ea8fe',               bg: 'rgba(110,168,254,0.09)',  border: 'rgba(110,168,254,0.22)' },
  PAID:         { label: 'Bezahlen',       color: '#00e5a0',               bg: 'rgba(0,229,160,0.09)',    border: 'rgba(0,229,160,0.22)'   },
  SCHEDULED:    { label: 'Geplant',        color: '#f97316',               bg: 'rgba(249,115,22,0.09)',   border: 'rgba(249,115,22,0.22)'  },
  LIVE:         { label: 'Live',           color: '#00e5a0',               bg: 'rgba(0,229,160,0.12)',    border: 'rgba(0,229,160,0.35)'   },
  PAUSED:       { label: 'Pausiert',       color: '#fbbf24',               bg: 'rgba(251,191,36,0.09)',   border: 'rgba(251,191,36,0.22)'  },
  ENDED:        { label: 'Beendet',        color: 'rgba(180,200,190,0.4)', bg: 'rgba(180,200,190,0.05)', border: 'rgba(180,200,190,0.1)'  },
  REJECTED:     { label: 'Abgelehnt',      color: '#ef4444',               bg: 'rgba(239,68,68,0.09)',    border: 'rgba(239,68,68,0.22)'   },
};

function AdStatusBadge({ status }) {
  const cfg = AD_STATUS_CONFIG[status] || AD_STATUS_CONFIG.DRAFT;
  const isLive = status === 'LIVE';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 10px', borderRadius: 5,
      background: cfg.bg, border: `1px solid ${cfg.border}`,
      color: cfg.color, fontSize: 10, fontWeight: 700,
      letterSpacing: '0.06em', textTransform: 'uppercase',
    }}>
      {isLive && <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#00e5a0', animation: 'adm-live-pulse 1.5s ease-in-out infinite' }} />}
      {cfg.label}
    </span>
  );
}

// ── Ad Report Modal ───────────────────────────────────────────────────────────
const MOCK_REPORT = {
  advertisementId: 42,
  displayName: 'ACME Sommeraktion',
  companyName: 'ACME GmbH',
  status: 'LIVE',
  generatedAt: new Date().toISOString(),
  period: { startsAt: '2026-05-01T00:00Z', endsAt: '2026-05-31T23:59:59Z' },
  metrics: { bookedImpressions: 10000, delivered: 4200, impressions: 3900, clicks: 184, ctr: 0.0472, averageViewDurationMs: 3860.5, remainingImpressions: 6100 },
  breakdowns: {
    byPlatform: [
      { key: 'iOS',     delivered: 2600, impressions: 2400, clicks: 115, ctr: 0.0479, averageViewDurationMs: 4100 },
      { key: 'Android', delivered: 1600, impressions: 1500, clicks:  69, ctr: 0.0460, averageViewDurationMs: 3520 },
    ],
    byLanguage: [
      { key: 'DE', delivered: 3100, impressions: 2900, clicks: 138, ctr: 0.0476, averageViewDurationMs: 3950 },
      { key: 'EN', delivered: 1100, impressions: 1000, clicks:  46, ctr: 0.0460, averageViewDurationMs: 3620 },
    ],
    byGender: [
      { key: 'Männlich', delivered: 2300, impressions: 2100, clicks:  88, ctr: 0.0419, averageViewDurationMs: 3700 },
      { key: 'Weiblich', delivered: 1700, impressions: 1600, clicks:  82, ctr: 0.0513, averageViewDurationMs: 4050 },
      { key: 'Divers',   delivered:  200, impressions:  200, clicks:  14, ctr: 0.0700, averageViewDurationMs: 4200 },
    ],
    byAgeGroup: [
      { key: '18–24', delivered: 1800, impressions: 1700, clicks:  92, ctr: 0.0541, averageViewDurationMs: 3500 },
      { key: '25–34', delivered: 1400, impressions: 1300, clicks:  61, ctr: 0.0469, averageViewDurationMs: 4000 },
      { key: '35–44', delivered:  600, impressions:  550, clicks:  21, ctr: 0.0382, averageViewDurationMs: 4300 },
      { key: '45+',   delivered:  400, impressions:  350, clicks:  10, ctr: 0.0286, averageViewDurationMs: 3800 },
    ],
  },
  dailyStats: [
    { date: '2026-05-09T00:00Z', delivered: 350, impressions: 320, clicks: 16, ctr: 0.0500, averageViewDurationMs: 3800 },
    { date: '2026-05-10T00:00Z', delivered: 420, impressions: 390, clicks: 19, ctr: 0.0487, averageViewDurationMs: 3900 },
    { date: '2026-05-11T00:00Z', delivered: 380, impressions: 350, clicks: 18, ctr: 0.0514, averageViewDurationMs: 3860 },
    { date: '2026-05-12T00:00Z', delivered: 460, impressions: 430, clicks: 22, ctr: 0.0512, averageViewDurationMs: 4100 },
    { date: '2026-05-13T00:00Z', delivered: 390, impressions: 360, clicks: 15, ctr: 0.0417, averageViewDurationMs: 3750 },
    { date: '2026-05-14T00:00Z', delivered: 410, impressions: 380, clicks: 20, ctr: 0.0526, averageViewDurationMs: 3950 },
  ],
};

function BarChart({ data, valueKey, labelKey, color = '#e8ede9', secondKey, secondColor = '#3d8b5e' }) {
  const [hovered, setHovered] = React.useState(null);
  const W = 680, H = 180, PL = 44, PR = 12, PT = 12, PB = 28;
  const chartW = W - PL - PR;
  const chartH = H - PT - PB;

  const maxVal = Math.max(...data.map(d => Math.max(d[valueKey] || 0, secondKey ? (d[secondKey] || 0) : 0)), 1);
  const niceMax = (() => {
    const mag = Math.pow(10, Math.floor(Math.log10(maxVal)));
    const steps = [1,2,5,10].map(s => s * mag);
    return steps.find(s => s >= maxVal) || maxVal;
  })();
  const GRID_LINES = 4;
  const barW = chartW / data.length;
  const barPad = Math.max(barW * 0.22, 6);

  const yLabel = (v) => v >= 1000 ? `${(v/1000).toFixed(v % 1000 === 0 ? 0 : 1)}k` : String(v);

  return (
    <div style={{ position:'relative', width:'100%' }}>
      {hovered !== null && (
        <div style={{ position:'absolute', top:0, left: Math.min(PL + hovered * barW + barW / 2, W - 120), transform:'translateX(-50%)', background:'#0c110e', border:'1px solid #2a3330', borderRadius:8, padding:'8px 12px', fontSize:11, color:'#e8ede9', zIndex:10, whiteSpace:'nowrap', pointerEvents:'none', boxShadow:'0 8px 24px rgba(0,0,0,0.5)' }}>
          <div style={{ fontWeight:700, marginBottom:5, color:'#8a9a8e', fontSize:10, textTransform:'uppercase', letterSpacing:'0.06em' }}>{data[hovered][labelKey]}</div>
          <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom: secondKey ? 3 : 0 }}>
            <div style={{ width:8, height:8, borderRadius:2, background:color, flexShrink:0 }}/>
            <span style={{ color:'#6a7a6e' }}>{valueKey === 'impressions' ? 'Impressionen' : valueKey === 'delivered' ? 'Ausgespielt' : 'Klicks'}:</span>
            <span style={{ fontWeight:700, color:'#e8ede9' }}>{(data[hovered][valueKey] || 0).toLocaleString('de-AT')}</span>
          </div>
          {secondKey && (
            <div style={{ display:'flex', alignItems:'center', gap:6 }}>
              <div style={{ width:8, height:8, borderRadius:2, background:secondColor, flexShrink:0 }}/>
              <span style={{ color:'#6a7a6e' }}>Klicks:</span>
              <span style={{ fontWeight:700, color:'#e8ede9' }}>{(data[hovered][secondKey] || 0).toLocaleString('de-AT')}</span>
            </div>
          )}
        </div>
      )}
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', height:'auto', display:'block', overflow:'visible' }}>
        {/* Gridlines + Y labels */}
        {Array.from({ length: GRID_LINES + 1 }).map((_, i) => {
          const val = Math.round((niceMax / GRID_LINES) * (GRID_LINES - i));
          const y = PT + (i / GRID_LINES) * chartH;
          return (
            <g key={i}>
              <line x1={PL} y1={y} x2={W - PR} y2={y} stroke="#1e2820" strokeWidth="1" />
              <text x={PL - 6} y={y + 4} textAnchor="end" fill="#3a4a3e" fontSize="9" fontFamily="system-ui,sans-serif">{yLabel(val)}</text>
            </g>
          );
        })}

        {/* Bars */}
        {data.map((d, i) => {
          const x = PL + i * barW;
          const v1 = d[valueKey] || 0;
          const v2 = secondKey ? (d[secondKey] || 0) : 0;
          const bh1 = (v1 / niceMax) * chartH;
          const bh2 = secondKey ? (v2 / niceMax) * chartH : 0;
          const isHov = hovered === i;

          return (
            <g key={i} style={{ cursor:'default' }}
              onMouseEnter={() => setHovered(i)} onMouseLeave={() => setHovered(null)}>
              {/* invisible hover target */}
              <rect x={x} y={PT} width={barW} height={chartH} fill="transparent" />

              {/* Impressions bar — full bar width, dim background */}
              <rect
                x={x + barPad} y={PT + chartH - Math.max(bh1, 2)}
                width={barW - barPad * 2} height={Math.max(bh1, 2)}
                rx="0" ry="0"
                fill={color} opacity={isHov ? 0.2 : 0.12}
                style={{ transition:'opacity 0.15s' }}
              />
              {/* Clicks bar — same width, solid, on top */}
              {secondKey && bh2 > 0 && (
                <rect
                  x={x + barPad} y={PT + chartH - Math.max(bh2, 2)}
                  width={barW - barPad * 2} height={Math.max(bh2, 2)}
                  rx="0" ry="0"
                  fill={secondColor}
                  opacity={1}
                  filter={isHov ? `drop-shadow(0 0 6px ${secondColor})` : 'none'}
                  style={{ transition:'filter 0.15s' }}
                />
              )}
              {/* Single bar (no secondKey) hover glow */}
              {!secondKey && bh1 > 0 && isHov && (
                <rect
                  x={x + barPad} y={PT + chartH - Math.max(bh1, 2)}
                  width={barW - barPad * 2} height={Math.max(bh1, 2)}
                  rx="0" ry="0"
                  fill={color} opacity={0.35}
                  filter={`drop-shadow(0 0 8px ${color})`}
                />
              )}

              {/* X label */}
              <text
                x={x + barW / 2} y={H - 4}
                textAnchor="middle" fill={isHov ? '#c8d5cb' : '#3a4a3e'}
                fontSize="9" fontFamily="system-ui,sans-serif"
                style={{ transition:'fill 0.15s' }}
              >{d[labelKey]}</text>
            </g>
          );
        })}

        {/* X axis line */}
        <line x1={PL} y1={PT + chartH} x2={W - PR} y2={PT + chartH} stroke="#2a3330" strokeWidth="1" />
      </svg>
    </div>
  );
}

function AdReportModal({ adId, adName, impressionLimit, mock, onClose }) {
  const [report, setReport] = React.useState(mock ? MOCK_REPORT : null);
  const [loading, setLoading] = React.useState(!mock);
  const [error, setError] = React.useState(null);
  const [breakdownTab, setBreakdownTab] = React.useState('byPlatform');
  const [chartMetric, setChartMetric] = React.useState('impressions');

  React.useEffect(() => {
    if (mock) return;
    AdvertisementAPI.getReport(adId)
      .then(setReport)
      .catch(() => setError('Report konnte nicht geladen werden.'))
      .finally(() => setLoading(false));
  }, [adId, mock]);

  const fmtMs  = (ms) => ms == null ? '—' : `${(ms / 1000).toFixed(1)}s`;
  const fmtPct = (v)  => v  != null ? `${(v * 100).toFixed(1)}%` : '—';
  const fmtNum = (v)  => v  != null ? v.toLocaleString('de-AT') : '—';
  const fmtKey = (k)  => {
    const map = { MALE:'Männlich', FEMALE:'Weiblich', DIVERSE:'Divers', OTHER:'Sonstige', UNKNOWN:'Unbekannt', DE:'Deutsch', EN:'Englisch', IOS:'iOS', ANDROID:'Android', UNDER_18:'Unter 18', AGE_18_24:'18–24', AGE_25_34:'25–34', AGE_35_44:'35–44', AGE_45_54:'45–54', AGE_55_PLUS:'55+' };
    return map[k] || k;
  };

  const BREAKDOWN_TABS = [
    { key: 'byPlatform', label: 'Plattform',    icon: <svg viewBox="0 0 24 24" width="11" height="11" fill="currentColor"><path d="M17 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2zm-5 17a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm4-4H8V5h8v10z"/></svg> },
    { key: 'byLanguage', label: 'Sprache',      icon: <svg viewBox="0 0 24 24" width="11" height="11" fill="currentColor"><path d="M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2zm-1 17.93V18a1 1 0 0 0-2 0v1.93A8.01 8.01 0 0 1 4.07 13H6a1 1 0 0 0 0-2H4.07A8.01 8.01 0 0 1 11 4.07V6a1 1 0 0 0 2 0V4.07A8.01 8.01 0 0 1 19.93 11H18a1 1 0 0 0 0 2h1.93A8.01 8.01 0 0 1 13 19.93V18a1 1 0 0 0-2 0v1.93z"/></svg> },
    { key: 'byGender',   label: 'Geschlecht',   icon: <svg viewBox="0 0 24 24" width="11" height="11" fill="currentColor"><path d="M12 2a5 5 0 1 0 0 10A5 5 0 0 0 12 2zm0 12c-5.33 0-8 2.67-8 4v1h16v-1c0-1.33-2.67-4-8-4z"/></svg> },
    { key: 'byAgeGroup', label: 'Altersgruppe', icon: <svg viewBox="0 0 24 24" width="11" height="11" fill="currentColor"><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/></svg> },
  ];

  const metrics = report?.metrics;
  const booked  = metrics?.bookedImpressions ?? impressionLimit ?? null;
  const deliveryPct = metrics ? Math.min(100, Math.round((metrics.impressions / (booked || 1)) * 100)) : 0;

  const dailyChartData = (report?.dailyStats || []).map(d => ({
    ...d,
    label: new Date(d.date).toLocaleDateString('de-AT', { day:'2-digit', month:'2-digit' }),
  }));

  const METRIC_CARDS = metrics ? [
    { label:'Impressionen',    value: fmtNum(metrics.impressions),           sub: `${fmtNum(booked)} gebucht`,    icon:<svg viewBox="0 0 24 24" width="15" height="15" fill="none"><path d="M12 5C7 5 2.73 8.11 1 12.5 2.73 16.89 7 20 12 20s9.27-3.11 11-7.5C21.27 8.11 17 5 12 5zm0 12.5a5 5 0 1 1 0-10 5 5 0 0 1 0 10zm0-8a3 3 0 1 0 0 6 3 3 0 0 0 0-6z" fill="currentColor"/></svg>, accent:'#e8ede9' },
    { label:'Klicks',          value: fmtNum(metrics.clicks),                sub: `CTR ${fmtPct(metrics.ctr)}`,       icon:<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>, accent:'#00ff88' },
    { label:'Ø Ansichtsdauer', value: fmtMs(metrics.averageViewDurationMs),  sub: 'pro Ausspielung',                  icon:<svg viewBox="0 0 24 24" width="15" height="15" fill="none"><path d="M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2zm0 18a8 8 0 1 1 0-16 8 8 0 0 1 0 16zm.5-13a1 1 0 0 0-1 1v4.382l-2.276 1.138a1 1 0 1 0 .894 1.788l2.724-1.362A1 1 0 0 0 13.5 13V8a1 1 0 0 0-1-1z" fill="currentColor"/></svg>, accent:'#3b82f6' },
    { label:'Verbleibend',     value: fmtNum(metrics.remainingImpressions),  sub: 'Impressionen offen',               icon:<svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor"><path d="M12 12a5 5 0 1 0 0-10 5 5 0 0 0 0 10zm0 2c-5.33 0-8 2.67-8 4v1h16v-1c0-1.33-2.67-4-8-4z"/></svg>, accent:'#f59e0b' },
  ] : [];

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999, padding:16 }}>
      <div style={{ background:'#111714', border:'1px solid #1e2820', borderRadius:16, width:'100%', maxWidth:780, maxHeight:'92vh', display:'flex', flexDirection:'column', boxShadow:'0 32px 100px rgba(0,0,0,0.7)' }}>

        {/* ── Header ── */}
        <div style={{ padding:'22px 26px 18px', borderBottom:'1px solid #1e2820', display:'flex', alignItems:'flex-start', justifyContent:'space-between', flexShrink:0 }}>
          <div>
            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:6 }}>
              <div style={{ width:32, height:32, borderRadius:8, background:'rgba(59,130,246,0.12)', border:'1px solid rgba(59,130,246,0.2)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="#6ea8fe" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>
              </div>
              <div>
                <div style={{ fontSize:15, fontWeight:700, color:'#e8ede9', lineHeight:1 }}>Campaign Report</div>
                <div style={{ fontSize:12, color:'#5a6a5e', marginTop:3 }}>{adName}{mock && <span style={{ marginLeft:8, fontSize:10, background:'rgba(249,115,22,0.15)', color:'#fb923c', border:'1px solid rgba(249,115,22,0.25)', borderRadius:4, padding:'1px 6px' }}>Demo</span>}</div>
              </div>
            </div>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:16 }}>
            {report?.period?.startsAt && (
              <div style={{ textAlign:'right' }}>
                <div style={{ fontSize:10, color:'#4a5a4e', textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:2 }}>Laufzeit</div>
                <div style={{ fontSize:12, color:'#8a9a8e', fontWeight:500 }}>
                  {new Date(report.period.startsAt).toLocaleDateString('de-AT', { day:'2-digit', month:'2-digit', year:'numeric' })}
                  {report.period?.endsAt && ` – ${new Date(report.period.endsAt).toLocaleDateString('de-AT', { day:'2-digit', month:'2-digit', year:'numeric' })}`}
                </div>
              </div>
            )}
            <button onClick={onClose} style={{ background:'rgba(255,255,255,0.05)', border:'1px solid #2a3330', borderRadius:8, color:'#6a7a6e', cursor:'pointer', padding:'6px 7px', display:'flex', alignItems:'center' }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16"><path d="M18 6L6 18M6 6l12 12"/></svg>
            </button>
          </div>
        </div>

        <div style={{ overflowY:'auto', padding:'22px 26px 26px' }}>
          {loading && (
            <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:10, padding:'60px 0', color:'#4a5a4e' }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18" style={{ animation:'spin 1s linear infinite' }}><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
              Report wird geladen…
            </div>
          )}
          {error && <div style={{ color:'#f87171', textAlign:'center', padding:'40px 0', fontSize:13 }}>{error}</div>}

          {report && <>
            {/* ── KPI Cards ── */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10, marginBottom:18 }}>
              {METRIC_CARDS.map(m => (
                <div key={m.label} style={{ background:'#161b18', border:'1px solid #1e2820', borderRadius:12, padding:'16px 16px 14px', position:'relative', overflow:'hidden' }}>
                  <div style={{ position:'absolute', top:0, left:0, right:0, height:40, background: `linear-gradient(to bottom, ${m.accent}18, transparent)`, borderRadius:'12px 12px 0 0', pointerEvents:'none' }} />
                  <div style={{ color: m.accent, marginBottom:10, opacity:0.9, lineHeight:0 }}>{m.icon}</div>
                  <div style={{ fontSize:22, fontWeight:800, color:'#e8ede9', lineHeight:1, marginBottom:4, letterSpacing:'-0.02em' }}>{m.value}</div>
                  <div style={{ fontSize:10, color:'#4a5a4e', textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:3 }}>{m.label}</div>
                  <div style={{ fontSize:10, color: m.accent, opacity:0.8 }}>{m.sub}</div>
                </div>
              ))}
            </div>

            {/* ── Delivery Bar ── */}
            <div style={{ background:'#161b18', border:'1px solid #1e2820', borderRadius:12, padding:'20px 22px', marginBottom:18 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', marginBottom:12 }}>
                <div>
                  <span style={{ fontSize:10, color:'#3a4a3e', textTransform:'uppercase', letterSpacing:'0.1em', display:'block', marginBottom:6 }}>Auslieferung</span>
                  <span style={{ fontSize:26, fontWeight:800, color:'#e8ede9', letterSpacing:'-0.03em', lineHeight:1, display:'block' }}>{deliveryPct}%</span>
                </div>
                <div />
              </div>
              <div style={{ position:'relative', height:6, background:'#1a1f1c', borderRadius:99, overflow:'visible', marginBottom:10 }}>
                <div style={{ height:6, width:`${deliveryPct}%`, background:'#e8ede9', borderRadius:99, transition:'width 0.6s ease', position:'relative' }}>
                  <div style={{ position:'absolute', right:-1, top:-3, width:12, height:12, borderRadius:'50%', background:'#e8ede9', boxShadow:'0 0 0 3px #161b18, 0 0 0 4px #e8ede940' }} />
                </div>
              </div>
              <div style={{ display:'flex', justifyContent:'space-between' }}>
                <span style={{ fontSize:11, color:'#e8ede9' }}>{fmtNum(metrics?.delivered)} ausgeliefert</span>
                {metrics?.remainingImpressions != null && <span style={{ fontSize:11, color:'#3a4a3e' }}>{fmtNum(metrics.remainingImpressions)} verbleibend</span>}
              </div>
            </div>

            {/* ── Daily Chart ── */}
            {dailyChartData.length > 0 && (
              <div style={{ background:'#161b18', border:'1px solid #1e2820', borderRadius:12, padding:'16px 18px 12px', marginBottom:18 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
                  <div style={{ fontSize:12, fontWeight:600, color:'#8a9a8e', textTransform:'uppercase', letterSpacing:'0.07em' }}>Tagesverlauf</div>
                  <div style={{ display:'flex', gap:16 }}>
                    {[{k:'impressions',l:'Impressions',c:'#e8ede9'},{k:'clicks',l:'Clicks',c:'#00ff88'}].map(o => {
                      const active = chartMetric === o.k;
                      return (
                        <button key={o.k} onClick={() => setChartMetric(o.k)} style={{
                          background:'none', border:'none', cursor:'pointer', padding:'2px 0',
                          fontSize:9, fontWeight: 500,
                          letterSpacing:'0.08em', textTransform:'uppercase',
                          color: active ? o.c : '#2e3e34',
                          borderBottom: active ? `1px solid ${o.c}` : '1px solid transparent',
                          transition:'all 0.15s',
                        }}>
                          {o.l}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <BarChart
                  data={dailyChartData}
                  valueKey={chartMetric}
                  secondKey={chartMetric === 'impressions' ? 'clicks' : undefined}
                  secondColor="#00ff88"
                  labelKey="label"
                  color={chartMetric === 'clicks' ? '#00ff88' : '#e8ede9'}
                />
                {chartMetric === 'impressions' && (
                  <div style={{ display:'flex', gap:14, marginTop:10, justifyContent:'flex-end' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:5, fontSize:10, color:'#4a5a4e' }}><div style={{ width:8, height:8, borderRadius:2, background:'#e8ede9' }}/> Impressionen</div>
                    <div style={{ display:'flex', alignItems:'center', gap:5, fontSize:10, color:'#4a5a4e' }}><div style={{ width:8, height:8, borderRadius:2, background:'#00ff88' }}/> Klicks</div>
                  </div>
                )}
              </div>
            )}

            {/* ── Breakdowns ── */}
            {report.breakdowns && (
              <div style={{ marginBottom:18 }}>
                <div style={{ fontSize:11, fontWeight:600, color:'#8a9a8e', textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:12 }}>Zielgruppen-Breakdown</div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                  {BREAKDOWN_TABS.map(t => {
                    const rows = report.breakdowns[t.key] || [];
                    if (!rows.length) return null;
                    const totalDel = rows.reduce((s, r) => s + (r.delivered || 0), 0) || 1;
                    return (
                      <div key={t.key} style={{ background:'#161b18', border:'1px solid #1e2820', borderRadius:12, overflow:'hidden' }}>
                        <div style={{ padding:'11px 16px 10px', borderBottom:'1px solid #1e2820', display:'flex', alignItems:'center', gap:7 }}>
                          <span style={{ color:'#4a5a4e' }}>{t.icon}</span>
                          <span style={{ fontSize:10, fontWeight:700, color:'#5a6a5e', textTransform:'uppercase', letterSpacing:'0.08em' }}>{t.label}</span>
                        </div>
                        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:11 }}>
                          <thead>
                            <tr style={{ background:'#0f1411' }}>
                              {['Segment','Impr.','Klicks','CTR'].map((h,i) => (
                                <th key={h} style={{ padding:'7px 12px', textAlign: i===0 ? 'left' : 'right', color:'#2e3e34', fontWeight:700, fontSize:9, textTransform:'uppercase', letterSpacing:'0.07em' }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map((row, i) => {
                              const barW = Math.round(((row.delivered || 0) / totalDel) * 100);
                              return (
                                <tr key={i} style={{ borderBottom:'1px solid #131a16' }}>
                                  <td style={{ padding:'9px 12px' }}>
                                    <div style={{ fontWeight:600, color:'#c8d5cb', fontSize:11, marginBottom:3 }}>{fmtKey(row.key)}</div>
                                    <div style={{ height:2, background:'#1e2820', borderRadius:99, width:60 }}>
                                      <div style={{ height:2, width:`${barW}%`, background:'#e8ede9', borderRadius:99 }} />
                                    </div>
                                  </td>
                                  <td style={{ padding:'9px 12px', textAlign:'right', color:'#5a6a5e', fontSize:11 }}>{fmtNum(row.impressions)}</td>
                                  <td style={{ padding:'9px 12px', textAlign:'right', color:'#5a6a5e', fontSize:11 }}>{fmtNum(row.clicks)}</td>
                                  <td style={{ padding:'9px 12px', textAlign:'right', color:'#00ff88', fontWeight:700, fontSize:11 }}>{fmtPct(row.ctr)}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── Footer meta ── */}
            {report.generatedAt && (
              <div style={{ fontSize:10, color:'#3a4a3e', textAlign:'center' }}>
                Report erstellt am {new Date(report.generatedAt).toLocaleString('de-AT', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' })}
              </div>
            )}
          </>}
        </div>
      </div>
    </div>
  );
}

// ── Edit Advertisement Modal ──────────────────────────────────────────────────
function EditAdvertisementModal({ ad, onClose, onSaved }) {
  const [form, setForm] = React.useState({
    displayName:    ad.displayName || '',
    descriptionDE:  ad.descriptionDE || '',
    descriptionEN:  ad.descriptionEN || '',
    actionUrl:      ad.actionUrl || '',
    startsAt:       toLocalDateInput(ad.startsAt),
    endsAt:         toLocalDateInput(ad.endsAt),
    impressionLimit: ad.impressionLimit || '',
  });
  const [imageDE, setImageDE] = React.useState(null);
  const [imageEN, setImageEN] = React.useState(null);
  const [uploadingDE, setUploadingDE] = React.useState(false);
  const [uploadingEN, setUploadingEN] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [err, setErr] = React.useState(null);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleImageUpload = async (file, language, setUploading, setFile) => {
    setUploading(true); setErr(null);
    try {
      await AdvertisementAPI.uploadAdminAsset(ad.id, file, language);
      setFile(file);
    } catch (e) {
      setErr(e?.response?.data?.message || `Bildupload (${language}) fehlgeschlagen.`);
    } finally { setUploading(false); }
  };

  const handleSave = async () => {
    if (!form.displayName.trim()) { setErr('Display Name ist erforderlich.'); return; }
    try {
      setSaving(true); setErr(null);
      const payload = {};
      if (form.displayName.trim())    payload.displayName    = form.displayName.trim();
      if (form.descriptionDE.trim())  payload.descriptionDE  = form.descriptionDE.trim();
      if (form.descriptionEN.trim())  payload.descriptionEN  = form.descriptionEN.trim();
      if (form.actionUrl.trim())      payload.actionUrl      = form.actionUrl.trim();
      // Dates go out only when changed: re-sending an unchanged one would move a campaign's
      // exact start or end to the edge of the day.
      if (form.startsAt && form.startsAt !== toLocalDateInput(ad.startsAt)) payload.startsAt = startOfLocalDay(form.startsAt);
      if (form.endsAt && form.endsAt !== toLocalDateInput(ad.endsAt))       payload.endsAt   = endOfLocalDay(form.endsAt);
      if (form.impressionLimit !== '' && form.impressionLimit !== null)
                                      payload.impressions = Number(form.impressionLimit);
      const updated = await AdvertisementAPI.updateAdvertisement(ad.id, payload);
      onSaved(updated);
    } catch (e) {
      setErr(e?.response?.data?.message || 'Speichern fehlgeschlagen.');
    } finally { setSaving(false); }
  };

  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const F = ({ label, children }) => (
    <div className="adm-modal-field">
      <label className="adm-modal-label">{label}</label>
      {children}
    </div>
  );

  return (
    <div className="adm-modal-overlay" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="adm-modal" style={{ width: 'min(600px,100%)' }}>
        <div className="adm-modal-header">
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <div className="adm-modal-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
            </div>
            <div>
              <div className="adm-modal-title">Werbung bearbeiten</div>
              <div className="adm-modal-sub">{ad.displayName}</div>
            </div>
          </div>
          <button className="adm-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="adm-modal-body">
          {err && <p style={{ fontSize: 12, color: 'rgba(255,100,100,0.9)', background: 'rgba(255,74,74,0.07)', border: '1px solid rgba(255,74,74,0.2)', borderRadius: 8, padding: '8px 12px', margin: 0 }}>{err}</p>}
          <F label="Display Name *">
            <input className="adm-modal-input" value={form.displayName} onChange={e => set('displayName', e.target.value)} />
          </F>
          <F label="Werbetext DE">
            <textarea className="adm-modal-textarea" value={form.descriptionDE} onChange={e => set('descriptionDE', e.target.value)} rows={2} />
          </F>
          <F label="Werbetext EN">
            <textarea className="adm-modal-textarea" value={form.descriptionEN} onChange={e => set('descriptionEN', e.target.value)} rows={2} />
          </F>
          <F label="Aktions-URL">
            <input className="adm-modal-input" type="url" value={form.actionUrl} onChange={e => set('actionUrl', e.target.value)} placeholder="https://…" />
          </F>
          <div className="adm-modal-row">
            <F label="Start">
              <input className="adm-modal-input" type="date" value={form.startsAt} onChange={e => set('startsAt', e.target.value)} />
            </F>
            <F label="Ende">
              <input className="adm-modal-input" type="date" value={form.endsAt} onChange={e => set('endsAt', e.target.value)} />
            </F>
          </div>
          <F label="Impression Limit">
            <input className="adm-modal-input" type="number" value={form.impressionLimit} onChange={e => set('impressionLimit', e.target.value)} placeholder="z. B. 10000" />
          </F>
          <div className="adm-modal-field">
            <label className="adm-modal-label">Werbebilder ersetzen</label>
            <div style={{ display:'flex', gap:10 }}>
              {[{ lang:'DE', file:imageDE, setFile:setImageDE, uploading:uploadingDE, setUploading:setUploadingDE },
                { lang:'EN', file:imageEN, setFile:setImageEN, uploading:uploadingEN, setUploading:setUploadingEN }
              ].map(({ lang, file, setFile, uploading, setUploading }) => (
                <label key={lang} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:6, padding:'12px 8px', border:`1px dashed ${file ? '#3d8b5e' : '#2e3530'}`, borderRadius:8, cursor:'pointer', background: file ? 'rgba(61,139,94,0.07)' : 'transparent', transition:'all 0.15s' }}>
                  <input type="file" accept="image/jpeg,image/png,image/webp" style={{ display:'none' }}
                    onChange={e => { const f = e.target.files?.[0]; if (f) handleImageUpload(f, lang, setUploading, setFile); e.target.value=''; }} />
                  {uploading
                    ? <span style={{ fontSize:11, color:'#6a7a6e' }}>Lädt hoch…</span>
                    : file
                      ? <><svg viewBox="0 0 24 24" fill="none" stroke="#3d8b5e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14"><polyline points="20 6 9 17 4 12"/></svg><span style={{ fontSize:11, color:'#3d8b5e', fontWeight:600 }}>{lang} hochgeladen</span></>
                      : <><svg viewBox="0 0 24 24" fill="none" stroke="#4a5a4e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg><span style={{ fontSize:11, color:'#4a5a4e' }}>{lang} Bild wählen</span></>
                  }
                </label>
              ))}
            </div>
            <div style={{ fontSize:10, color:'#4a5a4e', marginTop:5 }}>JPG, PNG oder WebP · max. 5 MB</div>
          </div>
        </div>
        <div className="adm-modal-footer">
          <button className="adm-modal-btn" onClick={onClose} disabled={saving}>Abbrechen</button>
          <button className="adm-modal-btn adm-modal-btn--primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Speichern…' : 'Speichern'}
          </button>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  return <span className={`adm-status adm-status--${(status || 'open').toLowerCase()}`}>{status || 'OPEN'}</span>;
}

// ── Create Session Modal ──────────────────────────────────────────────────────
function CreateSessionModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ clientName: '', impressions: '', notes: '', expiresAt: '' });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async () => {
    if (!form.clientName.trim()) { setErr('Kundenname ist erforderlich.'); return; }
    if (!form.impressions || Number(form.impressions) <= 0) { setErr('Bitte Anzahl Impressions angeben.'); return; }
    try {
      setLoading(true); setErr(null);
      const payload = {
        clientName: form.clientName.trim(),
        impressions: Number(form.impressions),
        expiresAt: form.expiresAt || null,
        notes: form.notes.trim() || null,
      };
      const session = await AdvertisementAPI.createSession(payload);
      onCreated(session);
    } catch (e) {
      setErr(e?.response?.data?.message || 'Fehler beim Erstellen.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="adm-modal-overlay" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="adm-modal">
        <div className="adm-modal-header">
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <div className="adm-modal-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                <rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/>
              </svg>
            </div>
            <div>
              <div className="adm-modal-title">Neue Upload-Session</div>
              <div className="adm-modal-sub">Erstellt einen geheimen Link für den Kunden</div>
            </div>
          </div>
          <button className="adm-modal-close" onClick={onClose}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>
        </div>

        <div className="adm-modal-body">
          {err && <p className="adm-error">{err}</p>}
          <div className="adm-modal-field">
            <label className="adm-modal-label">Kundenname *</label>
            <input className="adm-modal-input" value={form.clientName}
              onChange={e => set('clientName', e.target.value)} placeholder="z. B. ACME GmbH" />
          </div>
          <div className="adm-modal-row">
            <div className="adm-modal-field">
              <label className="adm-modal-label">Anzeigen-Typ</label>
              <div className="adm-modal-input" style={{ color: 'rgba(180,200,190,0.45)', cursor: 'default' }}>{AD_TYPE}</div>
            </div>
            <div className="adm-modal-field">
              <label className="adm-modal-label">Impressions *</label>
              <input className="adm-modal-input" type="number" min="1" value={form.impressions}
                onChange={e => set('impressions', e.target.value)} placeholder="z. B. 10000" />
            </div>
          </div>
          <div className="adm-modal-field">
            <label className="adm-modal-label">Gültig bis</label>
            <input className="adm-modal-input" type="date" value={form.expiresAt}
              onChange={e => set('expiresAt', e.target.value)} />
          </div>
        </div>

        <div className="adm-modal-footer">
          <button className="adm-modal-btn" onClick={onClose} disabled={loading}>Abbrechen</button>
          <button className="adm-modal-btn adm-modal-btn--primary" onClick={handleSubmit} disabled={loading}>
            {loading ? 'Erstelle…' : 'Session erstellen'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function AdvertisementAdmin() {
  const [sessions, setSessions]               = useState([]);
  const [submissions, setSubmissions]         = useState([]);
  const [advertisements, setAdvertisements]   = useState([]);
  const [loading, setLoading]                 = useState(false);
  const [error, setError]                     = useState(null);
  const [showCreate, setShowCreate]           = useState(false);
  const [successMsg, setSuccessMsg]           = useState(null);
  const [activeTab, setActiveTab]             = useState('sessions');
  const [confirmDelete, setConfirmDelete]     = useState({ open: false, id: null, type: '', name: '' });
  const [previewSub, setPreviewSub]           = useState(null);
  const [previewAd, setPreviewAd]             = useState(null);
  const [reviewAd, setReviewAd]               = useState(null);
  const [editAd, setEditAd]                   = useState(null);
  const [invoicePrompt, setInvoicePrompt]     = useState(null);
  const [invoiceAmount, setInvoiceAmount]     = useState('');
  const [invoiceOpenedFor, setInvoiceOpenedFor] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('invoiceOpenedFor') || '[]')); } catch { return new Set(); }
  });
  const markInvoiceOpened = (adId) => {
    setInvoiceOpenedFor(prev => {
      const next = new Set(prev).add(adId);
      localStorage.setItem('invoiceOpenedFor', JSON.stringify([...next]));
      return next;
    });
  };
  const [invoiceSentFor, setInvoiceSentFor] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('invoiceSentFor') || '[]')); } catch { return new Set(); }
  });
  const [invoiceSentPrompt, setInvoiceSentPrompt] = useState(null);
  const [invoiceSentTyped, setInvoiceSentTyped] = useState('');
  const markInvoiceSent = (adId) => {
    setInvoiceSentFor(prev => {
      const next = new Set(prev).add(adId);
      localStorage.setItem('invoiceSentFor', JSON.stringify([...next]));
      return next;
    });
  };
  const [invoiceSending, setInvoiceSending]   = useState(false);
  const [invoiceResult, setInvoiceResult]     = useState(null);
  const [invoiceMode, setInvoiceMode]         = useState('booked');
  const [invoicePreview, setInvoicePreview]   = useState(null);

  /* The figures shown before sending come from the server, because the tax
     treatment does: an EU business with a VAT id accounts for the tax itself,
     and a flat 20 % worked out here would show that customer a total they
     will never be billed. */
  useEffect(() => {
    if (!invoicePrompt?.adId) { setInvoicePreview(null); return; }
    const custom = invoiceMode === 'custom'
      ? Math.round((parseFloat(String(invoiceAmount).replace(',', '.')) || 0) * 100)
      : null;
    if (invoiceMode === 'custom' && !custom) { setInvoicePreview(null); return; }
    let cancelled = false;
    AdvertisementAPI.previewInvoice(invoicePrompt.adId, custom)
      .then(data => { if (!cancelled) setInvoicePreview(data); })
      .catch(() => { if (!cancelled) setInvoicePreview(null); });
    return () => { cancelled = true; };
  }, [invoicePrompt, invoiceMode, invoiceAmount]);

  /* Raises the invoice on the server and mails it in one step. The document is
     built there so the number is sequential, the figures are recorded against
     the campaign, and delivery does not depend on anyone remembering it. */
  const sendInvoice = async (adId, nettoRaw) => {
    const netto = parseFloat(String(nettoRaw).replace(',', '.'));
    const netAmountCents = Number.isFinite(netto) && netto > 0 ? Math.round(netto * 100) : null;
    setInvoiceSending(true);
    setInvoiceResult(null);
    try {
      const result = await AdvertisementAPI.sendInvoice(adId, netAmountCents);
      markInvoiceOpened(adId);
      markInvoiceSent(adId);
      setInvoiceResult({ ok: true, ...result });
      const updated = await AdvertisementAPI.updateAdvertisementStatus(adId, 'INVOICE_SENT');
      setAdvertisements(prev => prev.map(a => a.id === adId ? { ...a, ...updated } : a));
    } catch (error) {
      const detail = error?.response?.data?.detail
        || error?.response?.data?.message
        || error?.response?.data?.error;
      setInvoiceResult({ ok: false, message: detail || 'Rechnung konnte nicht gesendet werden.' });
    } finally {
      setInvoiceSending(false);
    }
  };

  const [statusConfirm, setStatusConfirm]     = useState(null);
  const [backConfirm, setBackConfirm]         = useState(null);
  const [backTyped, setBackTyped]             = useState('');
  const [reportAd, setReportAd]               = useState(null);
  const [showMockReport, setShowMockReport]   = useState(false);
  const [showLinkFor, setShowLinkFor]         = useState(null);
  const [copiedLink, setCopiedLink]           = useState(false);
  const [reportLinkLoading, setReportLinkLoading] = useState(null);


  /* The invoice used to be assembled here as HTML and opened in a window for
     the admin to print and mail by hand. The server renders and sends it now,
     so the number is sequential, the figures are recorded against the campaign,
     and delivery no longer depends on remembering it. */

  const buildLink = (token) => `${window.location.origin}/ad-upload/${token}`;
  const buildReportLink = (token) => `${window.location.origin}/advertisements/reports/${token}`;

  const fetchAll = useCallback(async () => {
    setLoading(true); setError(null);
    const safe = (p) => p.then(d => Array.isArray(d) ? d : []).catch(() => []);
    const [s, sub, ads] = await Promise.all([
      safe(AdvertisementAPI.getSessions()),
      safe(AdvertisementAPI.getSubmissions()),
      safe(AdvertisementAPI.getAdvertisements()),
    ]);
    setSessions(s);
    setSubmissions(sub);
    setAdvertisements(ads);
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleCreated = (session) => {
    setShowCreate(false);
    setSessions(prev => [session, ...prev]);
    setActiveTab('sessions');
    setSuccessMsg(`Session für „${session.clientName}" erfolgreich erstellt.`);
    setTimeout(() => setSuccessMsg(null), 4000);
  };

  const handleDeleteSession = async () => {
    const { id } = confirmDelete;
    setConfirmDelete({ open: false, id: null, type: '', name: '' });
    try {
      await AdvertisementAPI.deleteSession(id);
      setSessions(prev => prev.filter(s => s.id !== id));
    } catch { setError('Löschen fehlgeschlagen.'); }
  };

  const handleDeleteAd = async () => {
    const { id } = confirmDelete;
    setConfirmDelete({ open: false, id: null, type: '', name: '' });
    try {
      await AdvertisementAPI.deleteAdvertisement(id);
      setAdvertisements(prev => prev.filter(a => a.id !== id));
    } catch { setError('Löschen fehlgeschlagen.'); }
  };

  const handleAdStatus = async (adId, status) => {
    try {
      const updated = await AdvertisementAPI.updateAdvertisementStatus(adId, status);
      setAdvertisements(prev => prev.map(a => a.id === adId ? { ...a, ...updated } : a));
    } catch (error) {
      /* Accepting a campaign now raises and sends its invoice, and that can be
         refused for reasons only the server knows — an unconfigured sender, a
         booking with no agreed price, a mail server that would not take it.
         Swallowing the reason left "Status-Update fehlgeschlagen." as the only
         clue to a problem that names its own fix. */
      const detail = error?.response?.data?.detail
        || error?.response?.data?.message
        || error?.response?.data?.error;
      setError(detail ? `Status-Update fehlgeschlagen: ${detail}` : 'Status-Update fehlgeschlagen.');
    }
  };

  const handleAdSaved = (updated) => {
    setAdvertisements(prev => prev.map(a => a.id === updated.id ? { ...a, ...updated } : a));
    setEditAd(null);
  };

  const copyCustomerLink = (token) => {
    if (!token) return;
    setShowLinkFor(buildLink(token));
    setCopiedLink(false);
  };

  // Compute pending sessions (no submitted ad yet) for tab counts + Sessions tab
  const submissionToSession = {};
  submissions.forEach(s => { submissionToSession[s.id] = s.sessionId; });
  const sessionsWithAds = new Set(
    advertisements.map(a => submissionToSession[a.submissionId]).filter(Boolean)
  );
  const pendingSessions = sessions.filter(s => !sessionsWithAds.has(s.id));

  return (
    <div className="page">
      <div className="page-header">
        <PageTitle title="Werbung" description="Werbekampagnen, Buchungen und Rechnungen." />
      </div>

      <div className="adm-toolbar">
        {/* Tab bar */}
        <div className="adm-tabs">
          <button
            className={`adm-tab${activeTab === 'sessions' ? ' adm-tab--active' : ''}`}
            onClick={() => setActiveTab('sessions')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="13" height="13"><path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/><polyline points="13 2 13 9 20 9"/></svg>
            Upload Sessions
            {pendingSessions.length > 0 && <span className="adm-tab-badge">{pendingSessions.length}</span>}
          </button>
          <button
            className={`adm-tab${activeTab === 'kampagnen' ? ' adm-tab--active' : ''}`}
            onClick={() => setActiveTab('kampagnen')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="13" height="13"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
            Kampagnen
            {advertisements.length > 0 && <span className="adm-tab-badge">{advertisements.length}</span>}
          </button>
        </div>

        {/* Toolbar actions */}
        <div style={{ display: 'flex', gap: 8 }}>
          {activeTab === 'kampagnen' && <button
            className="btn-add btn-add--preview"
            title="Kundenansicht des Live Reports als Demo öffnen"
            onClick={() => window.open('/advertisements/reports/demo', '_blank')}
          >
            <svg className="btn-add-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
              <rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/>
            </svg>
            Kundenansicht
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="9" height="9" style={{ opacity:0.4, marginLeft:1 }}>
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
            </svg>
          </button>}
          {activeTab === 'sessions' && (
            <button className="btn-add btn-add--green" onClick={() => setShowCreate(true)}>
              <svg className="btn-add-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 5v14M5 12h14"/>
              </svg>
              Neue Session
            </button>
          )}
          <button className="btn-add btn-add--green" onClick={fetchAll} disabled={loading}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="13" height="13" className="btn-add-icon">
              <path d="M3 12a9 9 0 1 0 9-9"/><path d="M3 3v6h6"/>
            </svg>
            Neu laden
          </button>
        </div>
      </div>

      {error && <p className="adm-error">{error}</p>}
      {loading && <div className="adm-spinner" />}

      {/* ── Success toast ── */}
      {successMsg && (
        <div style={{ position:'fixed', bottom:28, right:28, zIndex:9999, display:'flex', alignItems:'center', gap:10, padding:'12px 16px', background:'#141918', border:'1px solid rgba(0,229,160,0.2)', borderRadius:12, boxShadow:'0 8px 32px rgba(0,0,0,0.45)', maxWidth:360, animation:'adm-toast-in 0.25s cubic-bezier(0.22,1,0.36,1)' }}>
          <div style={{ width:28, height:28, borderRadius:8, background:'rgba(0,229,160,0.1)', border:'1px solid rgba(0,229,160,0.2)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="#00e5a0" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="13" height="13"><polyline points="20 6 9 17 4 12"/></svg>
          </div>
          <span style={{ fontSize:12, fontWeight:500, color:'rgba(200,221,208,0.88)', lineHeight:1.4 }}>{successMsg}</span>
          <button onClick={() => setSuccessMsg(null)} style={{ marginLeft:'auto', background:'none', border:'none', color:'rgba(180,200,190,0.3)', cursor:'pointer', padding:2, display:'flex', flexShrink:0 }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="13" height="13"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>
          <style>{`@keyframes adm-toast-in { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }`}</style>
        </div>
      )}

      {/* ── Upload Sessions tab ── */}
      {!loading && activeTab === 'sessions' && (
        <>
          {pendingSessions.length === 0 ? (
            <div className="adm-empty">Keine offenen Upload Sessions.</div>
          ) : (
            <div className="adm-card">
              <table className="adm-table">
                <thead>
                  <tr>
                    <th>Kunde</th>
                    <th>Impressions</th>
                    <th>Status</th>
                    <th>Erstellt</th>
                    <th style={{ textAlign: 'right' }}>Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingSessions.map(s => (
                    <tr key={s.id}>
                      <td style={{ fontWeight: 600, color: 'rgba(220,235,228,0.88)' }}>{s.clientName}</td>
                      <td style={{ fontVariantNumeric: 'tabular-nums', color: 'rgba(0,229,160,0.7)', fontWeight: 600 }}>{s.impressions?.toLocaleString('de-AT') || '—'}</td>
                      <td><StatusBadge status={s.status} /></td>
                      <td style={{ color: 'rgba(180,200,190,0.4)', fontSize: 11 }}>{s.createdAt ? new Date(s.createdAt).toLocaleDateString('de-AT') : '—'}</td>
                      <td>
                        <div className="adm-actions">
                          {s.token && (
                            <button className="adm-btn adm-btn--ghost" onClick={() => copyCustomerLink(s.token)}>
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="11" height="11"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
                              Kundenlink
                            </button>
                          )}
                          <button className="adm-btn adm-btn--danger"
                            onClick={() => setConfirmDelete({ open: true, id: s.id, type: 'session', name: s.clientName })}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="11" height="11">
                              <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                              <path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ── Kampagnen tab ── */}
      {!loading && activeTab === 'kampagnen' && (
        <>
          {advertisements.length === 0 ? (
            <div className="adm-empty">Noch keine Kampagnen eingegangen.</div>
          ) : (
            <div className="adm-submissions">
              {advertisements.map(ad => {
                const sub = submissions.find(s => s.id === ad.submissionId);
                const sess = sessions.find(s => s.id === sub?.sessionId);
                const imgUrl = resolveAssetUrl(ad.advertisementImageDEUrl);
                const pct = ad.impressionLimit > 0 ? Math.min(100, (ad.impressionsDelivered / ad.impressionLimit) * 100) : 0;

            const statusCfg = AD_STATUS_CONFIG[ad.status] || AD_STATUS_CONFIG.DRAFT;

            return (
            <div key={ad.id} className={`adm-ad-card${ad.status === 'LIVE' ? ' adm-ad-card--live' : ''}`}>
              {ad.status === 'LIVE' && <span className="adm-live-beam" aria-hidden="true" />}

              {/* ── Main layout ── */}
              <div className="adm-ad-layout">

                {/* Thumbnail */}
                <div className="adm-ad-thumb" onClick={() => setPreviewAd(ad)}>
                  {imgUrl
                    ? <img src={imgUrl} alt="" className="adm-ad-thumb-img" />
                    : <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="24" height="24"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                  }
                  <div className="adm-ad-thumb-overlay">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20"><rect x="5" y="2" width="14" height="20" rx="2"/><line x1="12" y1="18" x2="12.01" y2="18"/></svg>
                  </div>
                </div>

                {/* Content */}
                <div className="adm-ad-content">

                  {/* Row 1: title + icon actions */}
                  <div className="adm-ad-header">
                    <div className="adm-ad-title">{ad.displayName || `#${ad.id}`}</div>
                    <div className="adm-ad-header-actions">
                      <button className="adm-ad-icon-btn" title="Bearbeiten" onClick={() => setEditAd(ad)}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="13" height="13"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                      </button>
                      <button className="adm-ad-icon-btn adm-ad-icon-btn--danger" title="Löschen" onClick={() => setConfirmDelete({ open: true, id: ad.id, type: 'ad', name: ad.displayName || `#${ad.id}` })}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="13" height="13"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                      </button>
                    </div>
                  </div>

                  {/* Row 2: meta chips */}
                  <div className="adm-ad-chips">
                    {ad.startsAt && (
                      <span className="adm-ad-chip adm-ad-chip--date">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="10" height="10"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                        {new Date(ad.startsAt).toLocaleDateString('de-AT')}
                        {' – '}
                        {ad.endsAt ? new Date(ad.endsAt).toLocaleDateString('de-AT') : '∞'}
                      </span>
                    )}
                  </div>

                  {/* Row 3: roadmap */}
                  <AdRoadmap status={ad.status} />

                  {/* Row 4: impression bar */}
                  {ad.impressionLimit > 0 && (
                    <div className="adm-ad-impr-row">
                      <div className="adm-ad-impr-bar">
                        <div className="adm-ad-impr-fill" style={{ width: `${pct}%`, background: statusCfg.color }} />
                      </div>
                      <span className="adm-ad-impr-label">
                        <strong>{(ad.impressionsDelivered || 0).toLocaleString('de-AT')}</strong>
                        <span> / {ad.impressionLimit.toLocaleString('de-AT')}</span>
                        <span className="adm-ad-impr-pct">{Math.round(pct)}%</span>
                      </span>
                    </div>
                  )}

                  {/* Row 5: actions */}
                  <div className="adm-ad-actions">
                    <div className="adm-ad-actions-left">
                      {ad.status === 'LIVE' && (
                        <button className="adm-ad-btn adm-ad-btn--ghost adm-ad-btn--back" onClick={() => { setBackConfirm({ adId: ad.id, status: 'PAID', label: 'Zurück zu Bezahlen', description: 'Die Kampagne wird auf Bezahlen zurückgesetzt.' }); setBackTyped(''); }}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="11" height="11"><polyline points="15 18 9 12 15 6"/></svg>
                          Zurück
                        </button>
                      )}
                      {ad.status === 'PAID' && (
                        <button className="adm-ad-btn adm-ad-btn--ghost adm-ad-btn--back" onClick={() => { setBackConfirm({ adId: ad.id, status: 'INVOICE_SENT', label: 'Zurück zu Rechnung', description: 'Die Kampagne wird auf Rechnung zurückgesetzt.' }); setBackTyped(''); }}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="11" height="11"><polyline points="15 18 9 12 15 6"/></svg>
                          Zurück
                        </button>
                      )}
                      {ad.status === 'INVOICE_SENT' && (
                        <button className="adm-ad-btn adm-ad-btn--ghost adm-ad-btn--back" onClick={() => { setBackConfirm({ adId: ad.id, status: 'DRAFT', label: 'Zurück zu Prüfung', description: 'Die Kampagne wird auf Prüfung zurückgesetzt.' }); setBackTyped(''); }}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="11" height="11"><polyline points="15 18 9 12 15 6"/></svg>
                          Zurück
                        </button>
                      )}
                      {['LIVE','PAUSED','ENDED'].includes(ad.status) && <button className="adm-ad-btn adm-ad-btn--pink" onClick={() => setReportAd({ id: ad.id, displayName: ad.displayName || `#${ad.id}`, impressionLimit: ad.impressionLimit })}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="12" height="12"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>
                        Report
                      </button>}
                      {['LIVE','PAUSED'].includes(ad.status) && (
                        <button
                          className="adm-ad-btn adm-ad-btn--rosewood"
                          disabled={reportLinkLoading === ad.id}
                          onClick={async () => {
                            setReportLinkLoading(ad.id);
                            try {
                              const link = await AdvertisementAPI.createReportLink(ad.id);
                              setShowLinkFor(buildReportLink(link.token));
                              setCopiedLink(false);
                            } catch { /* ignore */ } finally {
                              setReportLinkLoading(null);
                            }
                          }}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="12" height="12"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
                          {reportLinkLoading === ad.id ? 'Lädt…' : 'Live Report'}
                        </button>
                      )}
                      {sess?.token && <button className="adm-ad-btn adm-ad-btn--ghost" onClick={() => copyCustomerLink(sess.token)}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="12" height="12"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                        Kundenlink
                      </button>}
                    </div>
                    {sub && (
                      ad.status === 'DRAFT' ? (
                        <button className="adm-ad-btn--review" onClick={() => setReviewAd({ ad, sub, sess })}>
                          <svg viewBox="0 0 24 24" fill="currentColor" width="13" height="13"><path d="M12 4C7 4 2.73 7.11 1 12c1.73 4.89 6 8 11 8s9.27-3.11 11-8c-1.73-4.89-6-8-11-8zm0 13a5 5 0 1 1 0-10 5 5 0 0 1 0 10zm0-8a3 3 0 1 0 0 6 3 3 0 0 0 0-6z"/></svg>
                          Prüfen
                        </button>
                      ) : ad.status === 'PAID' ? (
                        <button className="adm-ad-btn--review adm-ad-btn--review-payment" onClick={() => setStatusConfirm({ adId: ad.id, status: 'LIVE', label: 'Zahlung ist eingegangen', description: 'Die Kampagne wird auf Live geschaltet.', color: '#3d8b5e' })}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="12" height="12"><path d="M20 6L9 17l-5-5"/></svg>
                          Zahlung eingegangen
                        </button>
                      ) : ad.status === 'INVOICE_SENT' ? (
                        <div style={{ display:'flex', gap:6 }}>
                          <button className="adm-ad-btn--review adm-ad-btn--review-invoice" onClick={() => { setInvoicePrompt({ sub, adId: ad.id }); setInvoiceAmount(''); setInvoiceMode('booked'); setInvoiceResult(null); }}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="12" height="12" style={{ color:'#fff' }}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                            Rechnung
                          </button>
                          <button
                            className={`adm-ad-btn--review adm-ad-btn--review-sent${!invoiceOpenedFor.has(ad.id) ? ' adm-ad-btn--review-sent-disabled' : ''}`}
                            disabled={!invoiceOpenedFor.has(ad.id)}
                            onClick={() => { setInvoiceSentPrompt({ adId: ad.id }); setInvoiceSentTyped(''); }}
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="12" height="12"><path d="M20 6L9 17l-5-5"/></svg>
                            Gesendet
                          </button>
                        </div>
                      ) : ad.status === 'LIVE' ? (
                        <div style={{ display:'flex', gap:6 }}>
                          <button className="adm-ad-btn adm-ad-btn--ghost" onClick={() => setStatusConfirm({ adId: ad.id, status: 'PAUSED', label: 'Kampagne pausieren', description: 'Die Kampagne wird pausiert und nicht mehr ausgespielt.' })}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="11" height="11"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
                            Pausieren
                          </button>
                          <button className="adm-ad-btn adm-ad-btn--danger" onClick={() => setStatusConfirm({ adId: ad.id, status: 'ENDED', label: 'Kampagne beenden', description: 'Die Kampagne wird dauerhaft beendet.' })}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="11" height="11"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                            Beenden
                          </button>
                        </div>
                      ) : ad.status === 'PAUSED' ? (
                        <div style={{ display:'flex', gap:6 }}>
                          <button className="adm-ad-btn adm-ad-btn--green" onClick={() => setStatusConfirm({ adId: ad.id, status: 'LIVE', label: 'Kampagne fortsetzen', description: 'Die Kampagne wird wieder live geschaltet und ausgespielt.', color: '#3d8b5e' })}>
                            <svg viewBox="0 0 24 24" fill="currentColor" width="11" height="11"><path d="M8 5v14l11-7z"/></svg>
                            Fortsetzen
                          </button>
                          <button className="adm-ad-btn adm-ad-btn--danger" onClick={() => setStatusConfirm({ adId: ad.id, status: 'ENDED', label: 'Kampagne beenden', description: 'Die Kampagne wird dauerhaft beendet.' })}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="11" height="11"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                            Beenden
                          </button>
                        </div>
                      ) : null
                    )}
                  </div>
                </div>
              </div>

              {/* ── Expandable customer details ── */}
            </div>
            );
              })}
            </div>
          )}
        </>
      )}

      {showLinkFor && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', backdropFilter:'blur(4px)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999 }}
          onMouseDown={e => { if (e.target === e.currentTarget) setShowLinkFor(null); }}>
          <div style={{ background:'#141918', border:'1px solid rgba(255,255,255,0.08)', borderRadius:16, width:500, boxShadow:'0 32px 80px rgba(0,0,0,0.6)', overflow:'hidden' }}>

            {/* Header */}
            <div style={{ padding:'20px 24px 16px', borderBottom:'1px solid rgba(255,255,255,0.06)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <div style={{ width:32, height:32, borderRadius:8, background:'rgba(110,168,254,0.1)', border:'1px solid rgba(110,168,254,0.2)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="#6ea8fe" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
                </div>
                <div>
                  <div style={{ fontSize:13, fontWeight:700, color:'rgba(220,235,228,0.95)', lineHeight:1 }}>Kundenlink</div>
                  <div style={{ fontSize:11, color:'rgba(180,200,190,0.4)', marginTop:3 }}>Diesen Link an den Kunden weitergeben</div>
                </div>
              </div>
              <button onClick={() => setShowLinkFor(null)} style={{ background:'none', border:'none', color:'rgba(180,200,190,0.3)', cursor:'pointer', padding:4, display:'flex', alignItems:'center', borderRadius:6 }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
            </div>

            {/* Body */}
            <div style={{ padding:'20px 24px 24px' }}>
              <div style={{ fontSize:10, fontWeight:600, letterSpacing:'0.08em', textTransform:'uppercase', color:'rgba(180,200,190,0.35)', marginBottom:8 }}>URL</div>
              <div style={{ background:'rgba(0,0,0,0.25)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:10, padding:'12px 14px', marginBottom:16, wordBreak:'break-all', fontSize:12, color:'rgba(200,221,208,0.75)', fontFamily:'monospace', lineHeight:1.55, userSelect:'all', cursor:'text' }}>
                {showLinkFor}
              </div>
              <div style={{ display:'flex', gap:8 }}>
                <button
                  onClick={() => { navigator.clipboard.writeText(showLinkFor); setCopiedLink(true); setTimeout(() => setCopiedLink(false), 2000); }}
                  style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:7, padding:'10px 0', borderRadius:9, border:'none', background: copiedLink ? 'rgba(0,229,160,0.12)' : 'rgba(255,255,255,0.06)', color: copiedLink ? '#00e5a0' : 'rgba(200,221,208,0.8)', fontSize:12, fontWeight:600, cursor:'pointer', transition:'all 0.15s', outline: copiedLink ? '1px solid rgba(0,229,160,0.3)' : '1px solid transparent' }}
                >
                  {copiedLink
                    ? <><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="13" height="13"><polyline points="20 6 9 17 4 12"/></svg> Kopiert</>
                    : <><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="13" height="13"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg> Link kopieren</>
                  }
                </button>
                <button
                  onClick={() => window.open(showLinkFor, '_blank', 'noopener,noreferrer')}
                  style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:6, padding:'10px 16px', borderRadius:9, border:'1px solid rgba(255,255,255,0.08)', background:'transparent', color:'rgba(180,200,190,0.5)', fontSize:12, fontWeight:500, cursor:'pointer', transition:'all 0.15s' }}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="12" height="12"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                  Öffnen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {previewSub && <AdPreviewModal sub={previewSub} onClose={() => setPreviewSub(null)} />}
      {reviewAd && (
        <ReviewModal
          ad={reviewAd.ad}
          sub={reviewAd.sub}
          sess={reviewAd.sess}
          onClose={() => setReviewAd(null)}
          onStatusAction={(adId, status, label, description, color) => {
            setReviewAd(null);
            setStatusConfirm({ adId, status, label, description, color });
          }}
          onInvoice={(sub, adId) => {
            setReviewAd(null);
            setInvoicePrompt({ sub, adId });
            setInvoiceAmount('');
          }}
        />
      )}
      {previewAd  && <AdPreviewModal sub={previewAd}  onClose={() => setPreviewAd(null)} />}
      {showCreate && <CreateSessionModal onClose={() => setShowCreate(false)} onCreated={handleCreated} />}
      {editAd     && <EditAdvertisementModal ad={editAd} onClose={() => setEditAd(null)} onSaved={handleAdSaved} />}
      {reportAd        && <AdReportModal adId={reportAd.id} adName={reportAd.displayName} impressionLimit={reportAd.impressionLimit} onClose={() => setReportAd(null)} />}
      {showMockReport  && <AdReportModal mock adName="ACME Sommeraktion (Demo)" onClose={() => setShowMockReport(false)} />}

      {statusConfirm && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', backdropFilter:'blur(10px)', WebkitBackdropFilter:'blur(10px)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999 }}
          onMouseDown={e => { if (e.target === e.currentTarget) setStatusConfirm(null); }}>
          <div style={{ background:'#0c1410', border:'1px solid rgba(255,255,255,0.07)', borderRadius:18, padding:'32px 28px 24px', width:340, boxShadow:'0 40px 80px rgba(0,0,0,0.7)' }}>
            <div style={{ width:38, height:38, borderRadius:10, background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.09)', display:'flex', alignItems:'center', justifyContent:'center', marginBottom:20, color:'rgba(180,210,195,0.5)' }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="17" height="17"><path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"/><path d="M12 8v4m0 4h.01"/></svg>
            </div>
            <div style={{ fontSize:14, fontWeight:600, color:'rgba(225,240,232,0.95)', marginBottom:7, letterSpacing:'-0.01em' }}>{statusConfirm.label}</div>
            <div style={{ fontSize:12, color:'rgba(180,200,190,0.42)', lineHeight:1.65, marginBottom:28 }}>{statusConfirm.description}</div>
            <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
              <button autoFocus onClick={() => setStatusConfirm(null)} style={{ padding:'0 16px', height:34, borderRadius:999, border:'1px solid rgba(255,255,255,0.08)', background:'rgba(255,255,255,0.03)', color:'rgba(180,200,190,0.45)', cursor:'pointer', fontSize:12, fontFamily:'inherit', fontWeight:500 }}>Abbrechen</button>
              <button
                onClick={() => { handleAdStatus(statusConfirm.adId, statusConfirm.status); setStatusConfirm(null); }}
                style={{ padding:'0 20px', height:34, borderRadius:999, border:'1px solid rgba(139,92,246,0.4)', background:'linear-gradient(135deg,#6366f1,#8b5cf6,#ec4899)', color:'#fff', cursor:'pointer', fontSize:12, fontFamily:'inherit', fontWeight:600, letterSpacing:'0.01em', boxShadow:'0 2px 12px rgba(139,92,246,0.4)' }}
              >{statusConfirm.label}</button>
            </div>
          </div>
        </div>
      )}

      {backConfirm && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.72)', backdropFilter:'blur(10px)', WebkitBackdropFilter:'blur(10px)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999 }}
          onMouseDown={e => { if (e.target === e.currentTarget) { setBackConfirm(null); setBackTyped(''); } }}>
          <div style={{ background:'#0c1410', border:'1px solid rgba(255,255,255,0.07)', borderRadius:18, padding:'28px', width:360, boxShadow:'0 40px 80px rgba(0,0,0,0.7)' }}>
            <div style={{ width:36, height:36, borderRadius:9, background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.18)', display:'flex', alignItems:'center', justifyContent:'center', marginBottom:18, color:'rgba(239,100,100,0.7)' }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="17" height="17"><polyline points="15 18 9 12 15 6"/></svg>
            </div>
            <div style={{ fontSize:14, fontWeight:600, color:'rgba(225,240,232,0.95)', marginBottom:6, letterSpacing:'-0.01em' }}>{backConfirm.label}</div>
            <div style={{ fontSize:12, color:'rgba(180,200,190,0.42)', lineHeight:1.65, marginBottom:20 }}>{backConfirm.description}</div>
            <div style={{ marginBottom:20 }}>
              <label style={{ display:'block', fontSize:11, color:'rgba(180,200,190,0.4)', marginBottom:7 }}>
                Zur Bestätigung <span style={{ fontFamily:'ui-monospace,monospace', color:'rgba(239,100,100,0.65)' }}>ZURÜCK</span> eingeben
              </label>
              <input
                autoFocus
                value={backTyped}
                onChange={e => setBackTyped(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Escape') { setBackConfirm(null); setBackTyped(''); }
                  if (e.key === 'Enter' && backTyped === 'ZURÜCK') { handleAdStatus(backConfirm.adId, backConfirm.status); setBackConfirm(null); setBackTyped(''); }
                }}
                placeholder="ZURÜCK"
                spellCheck={false}
                style={{ width:'100%', boxSizing:'border-box', background:'rgba(239,68,68,0.04)', border:'1px solid rgba(239,68,68,0.18)', borderRadius:9, padding:'9px 12px', fontSize:13, fontFamily:'ui-monospace,monospace', color:'rgba(220,235,228,0.9)', outline:'none', letterSpacing:'0.05em' }}
              />
            </div>
            <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
              <button onClick={() => { setBackConfirm(null); setBackTyped(''); }} style={{ padding:'0 16px', height:34, borderRadius:999, border:'1px solid rgba(255,255,255,0.08)', background:'rgba(255,255,255,0.03)', color:'rgba(180,200,190,0.45)', cursor:'pointer', fontSize:12, fontFamily:'inherit', fontWeight:500 }}>Abbrechen</button>
              <button
                disabled={backTyped !== 'ZURÜCK'}
                onClick={() => { handleAdStatus(backConfirm.adId, backConfirm.status); setBackConfirm(null); setBackTyped(''); }}
                style={{ padding:'0 20px', height:34, borderRadius:999, border:'1px solid rgba(239,68,68,0.35)', background: backTyped === 'ZURÜCK' ? 'linear-gradient(135deg,rgba(239,68,68,0.85),rgba(220,38,38,0.9))' : 'rgba(255,255,255,0.03)', color: backTyped === 'ZURÜCK' ? '#fff' : 'rgba(180,200,190,0.25)', cursor: backTyped === 'ZURÜCK' ? 'pointer' : 'default', fontSize:12, fontFamily:'inherit', fontWeight:600, transition:'all 0.15s' }}
              >Bestätigen</button>
            </div>
          </div>
        </div>
      )}

      {invoicePrompt && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.72)', backdropFilter:'blur(10px)', WebkitBackdropFilter:'blur(10px)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999 }}
          onMouseDown={e => { if (e.target === e.currentTarget) setInvoicePrompt(null); }}>
          <div style={{ background:'#0c1410', border:'1px solid rgba(255,255,255,0.07)', borderRadius:18, padding:'28px', width:380, boxShadow:'0 40px 80px rgba(0,0,0,0.7)' }}>

            {/* Header */}
            <div style={{ marginBottom:24 }}>
              <div style={{ fontSize:15, fontWeight:600, letterSpacing:'-0.01em', color:'rgba(235,248,240,0.95)', marginBottom:10 }}>Rechnungserstellung</div>
              <div style={{ display:'flex', gap:4, padding:4, borderRadius:10, background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.07)' }}>
                {[
                  { key:'booked', label:'Gebuchter Preis' },
                  { key:'custom', label:'Anderer Betrag' },
                ].map(opt => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setInvoiceMode(opt.key)}
                    style={{ flex:1, height:32, borderRadius:7, border:0, cursor:'pointer', fontSize:12, fontFamily:'inherit', fontWeight:600, transition:'all 0.15s',
                      background: invoiceMode === opt.key ? 'linear-gradient(135deg,#2d6a4f,#1b4332)' : 'transparent',
                      color: invoiceMode === opt.key ? '#fff' : 'rgba(180,200,190,0.5)' }}
                  >{opt.label}</button>
                ))}
              </div>
            </div>

            {/* Input */}
            {invoiceMode === 'custom' ? (

            <div style={{ position:'relative', marginBottom:16 }}>
              <span style={{ position:'absolute', left:14, top:'50%', transform:'translateY(-50%)', color:'rgba(180,200,190,0.5)', fontSize:15, fontWeight:500, pointerEvents:'none' }}>€</span>
              <input
                type="number"
                min="0"
                step="0.01"
                placeholder="0,00"
                value={invoiceAmount}
                onChange={e => setInvoiceAmount(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !invoiceSending) sendInvoice(invoicePrompt.adId, invoiceAmount); }}
                autoFocus
                style={{ width:'100%', background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.09)', borderRadius:10, padding:'13px 14px 13px 30px', color:'rgba(230,244,236,0.95)', fontSize:22, fontWeight:600, outline:'none', boxSizing:'border-box', letterSpacing:'-0.02em', transition:'border-color 0.15s' }}
                onFocus={e => e.target.style.borderColor='rgba(45,106,79,0.5)'}
                onBlur={e => e.target.style.borderColor='rgba(255,255,255,0.09)'}
              />
            </div>
            ) : (
              <div style={{ marginBottom:16, padding:'13px 14px', borderRadius:10, background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.09)', fontSize:12, color:'rgba(180,200,190,0.5)', lineHeight:1.6 }}>
                {invoicePreview?.bookedNetCents
                  ? 'Der Preis, den der Kunde beim Buchen gesehen hat.'
                  : 'Für diese Buchung ist kein Preis hinterlegt — wähle „Anderer Betrag“.'}
              </div>
            )}

            {/* Breakdown */}
            {invoicePreview && invoicePreview.netCents > 0 && (() => {
              const fmt = cents => (cents / 100).toLocaleString('de-AT', { minimumFractionDigits:2, maximumFractionDigits:2 });
              return (
                <div style={{ borderRadius:10, border:'1px solid rgba(255,255,255,0.06)', overflow:'hidden', marginBottom:20 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 14px', borderBottom:'1px solid rgba(255,255,255,0.05)' }}>
                    <span style={{ fontSize:12, color:'rgba(180,200,190,0.45)' }}>Nettobetrag</span>
                    <span style={{ fontSize:13, fontWeight:500, color:'rgba(210,228,218,0.8)', fontVariantNumeric:'tabular-nums' }}>€ {fmt(invoicePreview.netCents)}</span>
                  </div>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 14px', borderBottom:'1px solid rgba(255,255,255,0.05)' }}>
                    <span style={{ fontSize:12, color:'rgba(180,200,190,0.45)' }}>
                      {invoicePreview.reverseCharge ? 'Reverse Charge' : `MwSt. ${invoicePreview.vatRatePercent} %`}
                    </span>
                    <span style={{ fontSize:13, fontWeight:500, color:'rgba(210,228,218,0.8)', fontVariantNumeric:'tabular-nums' }}>€ {fmt(invoicePreview.vatCents)}</span>
                  </div>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'12px 14px', background:'rgba(255,255,255,0.025)' }}>
                    <span style={{ fontSize:12, fontWeight:700, color:'rgba(200,220,210,0.7)', letterSpacing:'0.04em', textTransform:'uppercase' }}>Gesamt</span>
                    <span style={{ fontSize:16, fontWeight:700, color:'rgba(230,244,236,0.97)', letterSpacing:'-0.02em', fontVariantNumeric:'tabular-nums' }}>€ {fmt(invoicePreview.grossCents)}</span>
                  </div>
                  <div style={{ padding:'9px 14px', borderTop:'1px solid rgba(255,255,255,0.05)', fontSize:11, color:'rgba(180,200,190,0.4)', lineHeight:1.55 }}>
                    Geht an {invoicePreview.recipientEmail}
                    {invoicePreview.reverseCharge ? ' · Steuerschuldnerschaft des Leistungsempfängers' : ''}
                  </div>
                </div>
              );
            })()}

            {/* Result */}
            {invoiceResult && (
              <div style={{ marginBottom:16, padding:'11px 13px', borderRadius:10, border:`1px solid ${invoiceResult.ok ? 'rgba(45,106,79,0.45)' : 'rgba(239,68,68,0.35)'}`, background: invoiceResult.ok ? 'rgba(45,106,79,0.12)' : 'rgba(239,68,68,0.09)', fontSize:12, lineHeight:1.6, color: invoiceResult.ok ? 'rgba(200,235,215,0.9)' : 'rgba(250,200,200,0.9)' }}>
                {invoiceResult.ok
                  ? `Rechnung ${invoiceResult.invoiceNumber} an ${invoiceResult.recipient} gesendet.`
                  : invoiceResult.message}
              </div>
            )}

            {/* Actions */}
            <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
              <button onClick={() => { setInvoicePrompt(null); setInvoiceResult(null); }} style={{ padding:'0 16px', height:36, borderRadius:999, border:'1px solid rgba(255,255,255,0.08)', background:'rgba(255,255,255,0.03)', color:'rgba(180,200,190,0.45)', cursor:'pointer', fontSize:12, fontFamily:'inherit', fontWeight:500 }}>
                {invoiceResult?.ok ? 'Schließen' : 'Abbrechen'}
              </button>
              {!invoiceResult?.ok && (
                <button
                  disabled={invoiceSending}
                  onClick={() => sendInvoice(invoicePrompt.adId, invoiceMode === 'custom' ? invoiceAmount : null)}
                  style={{ padding:'0 20px', height:36, borderRadius:999, border:'1px solid rgba(45,106,79,0.5)', background: invoiceSending ? 'rgba(255,255,255,0.03)' : 'linear-gradient(135deg,#2d6a4f,#1b4332)', color: invoiceSending ? 'rgba(180,200,190,0.35)' : '#fff', cursor: invoiceSending ? 'wait' : 'pointer', fontSize:12, fontFamily:'inherit', fontWeight:600, boxShadow: invoiceSending ? 'none' : '0 2px 12px rgba(27,67,50,0.5)', transition:'all 0.15s' }}
                >{invoiceSending ? 'Wird gesendet …' : 'Rechnung senden'}</button>
              )}
            </div>

          </div>
        </div>
      )}

      {invoiceSentPrompt && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.72)', backdropFilter:'blur(10px)', WebkitBackdropFilter:'blur(10px)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:10001 }}
          onMouseDown={e => { if (e.target === e.currentTarget) setInvoiceSentPrompt(null); }}>
          <div style={{ background:'#0c1410', border:'1px solid rgba(255,255,255,0.07)', borderRadius:18, padding:'28px', width:380, boxShadow:'0 40px 80px rgba(0,0,0,0.7)' }}>
            <div style={{ width:38, height:38, borderRadius:10, background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.09)', display:'flex', alignItems:'center', justifyContent:'center', marginBottom:18, color:'rgba(180,210,195,0.5)' }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="17" height="17"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
            </div>
            <div style={{ fontSize:15, fontWeight:600, color:'rgba(225,240,232,0.95)', marginBottom:6, letterSpacing:'-0.01em' }}>Zahlung eingegangen?</div>
            <div style={{ fontSize:12, color:'rgba(180,200,190,0.42)', lineHeight:1.65, marginBottom:20 }}>Die Rechnung verschickt der Server beim Erstellen. Bestätige hier, dass die Zahlung eingegangen ist — die Kampagne wird auf bezahlt gesetzt.</div>
            <div style={{ display:'flex', flexDirection:'column', gap:6, marginBottom:20 }}>
              <label style={{ fontSize:11, fontWeight:400, color:'rgba(180,200,190,0.45)' }}>Zur Bestätigung <span style={{ color:'rgba(230,244,236,0.75)', fontFamily:'ui-monospace,monospace', fontWeight:600, letterSpacing:'0.06em' }}>GESENDET</span> eingeben</label>
              <input
                autoFocus
                type="text"
                value={invoiceSentTyped}
                onChange={e => setInvoiceSentTyped(e.target.value)}
                onKeyDown={e => { if (e.key === 'Escape') setInvoiceSentPrompt(null); if (e.key === 'Enter' && invoiceSentTyped === 'GESENDET') { markInvoiceSent(invoiceSentPrompt.adId); handleAdStatus(invoiceSentPrompt.adId, 'PAID'); setInvoiceSentPrompt(null); }}}
                placeholder="GESENDET"
                spellCheck={false}
                autoComplete="off"
                style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.09)', borderRadius:10, padding:'11px 14px', color:'rgba(230,244,236,0.95)', fontSize:14, fontFamily:'ui-monospace,monospace', letterSpacing:'0.08em', outline:'none', boxSizing:'border-box', width:'100%', transition:'border-color 0.15s' }}
                onFocus={e => e.target.style.borderColor='rgba(45,106,79,0.5)'}
                onBlur={e => e.target.style.borderColor='rgba(255,255,255,0.09)'}
              />
            </div>
            <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
              <button onClick={() => setInvoiceSentPrompt(null)} style={{ padding:'0 16px', height:34, borderRadius:999, border:'1px solid rgba(255,255,255,0.08)', background:'rgba(255,255,255,0.03)', color:'rgba(180,200,190,0.45)', cursor:'pointer', fontSize:12, fontFamily:'inherit', fontWeight:500 }}>Abbrechen</button>
              <button
                disabled={invoiceSentTyped !== 'GESENDET'}
                onClick={() => { markInvoiceSent(invoiceSentPrompt.adId); handleAdStatus(invoiceSentPrompt.adId, 'PAID'); setInvoiceSentPrompt(null); }}
                style={{ padding:'0 20px', height:34, borderRadius:999, border:'1px solid rgba(45,106,79,0.5)', background: invoiceSentTyped === 'GESENDET' ? 'linear-gradient(135deg,#2d6a4f,#1b4332)' : 'rgba(255,255,255,0.03)', color: invoiceSentTyped === 'GESENDET' ? '#fff' : 'rgba(180,200,190,0.25)', cursor: invoiceSentTyped === 'GESENDET' ? 'pointer' : 'default', fontSize:12, fontFamily:'inherit', fontWeight:600, transition:'all 0.15s' }}
              >Bestätigen</button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDeleteModal
        open={confirmDelete.open}
        title={`"${confirmDelete.name}" löschen?`}
        message={confirmDelete.type === 'session'
          ? 'Die Session und der geheime Link werden dauerhaft gelöscht.'
          : confirmDelete.type === 'ad'
          ? 'Die Werbung wird dauerhaft gelöscht.'
          : 'Die Submission wird dauerhaft gelöscht.'}
        onConfirm={
          confirmDelete.type === 'session' ? handleDeleteSession : handleDeleteAd
        }
        onCancel={() => setConfirmDelete({ open: false, id: null, type: '', name: '' })}
      />
    </div>
  );
}
